import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, ArrowUpRight, Lock } from "lucide-react";
import { Reveal, MaskedLines, Eyebrow } from "@/components/motion";
import { api, formatApiError } from "@/lib/api";

const PLANS = {
  monthly: [
    { id: "m-starter", name: "Skool Community", price: 97, setup: 0, external_url: "https://www.skool.com/cpr-credit-mentorship-7667/about", tagline: "Credit mentorship community",
      features: ["Weekly live mentorship calls", "Step-by-step credit building education", "Done-for-you credit repair support", "Advanced funding strategies", "Auto-financing preparation", "Private community support"] },
    { id: "m-pro", lookup_key: "pro_monthly", name: "Prestigious", price: 189, setup: 0, popular: true, tagline: "Our most chosen plan",
      features: ["Everything in the Skool Community", "Unlimited disputes / cycle", "Inquiry removal engine", "Metro 2 compliance auditing", "Priority phone support", "Monthly strategy call"] },
    { id: "m-vip", lookup_key: "vip_monthly", name: "Diamond", price: 299, setup: 0, tagline: "Credit + capital concierge",
      features: ["Everything in Prestigious", "Dedicated senior credit architect", "Business credit building", "Funding readiness program", "Lender introductions", "24/7 concierge line"] },
  ],
  flat: [
    { id: "f-express", lookup_key: "express_annual", name: "Basic", price: 999, tagline: "One sweep, 45 days",
      features: ["Single intensive dispute cycle", "Up to 10 negative items", "Collections & late payments focus", "45-day completion target", "Progress updates by email & text"] },
    { id: "f-overhaul", lookup_key: "overhaul_annual", name: "Prestigious", price: 1499, popular: true, tagline: "The full reconstruction",
      features: ["Three full dispute cycles", "Unlimited negative items", "Bankruptcy & repossession auditing", "Score rebuild playbook", "6 months of monitoring"] },
    { id: "f-bundle", lookup_key: "bundle_annual", name: "Diamond", price: 2499, tagline: "Repair, then get funded",
      features: ["Everything in Prestigious", "Business credit profile build-out", "Funding pre-qualification", "$25k to $250k lender matching", "Dedicated funding advisor", "12 months of support"] },
  ],
};

const COMPARISON = [
  ["Bureau audit & analysis", true, true, true],
  ["Negative item disputes", "5 / cycle", "Unlimited", "Unlimited"],
  ["Metro 2 compliance auditing", false, true, true],
  ["Inquiry removal", false, true, true],
  ["Business credit building", false, false, true],
  ["Funding desk access", false, "Add-on", true],
  ["Dedicated architect", false, false, true],
];

export default function Pricing() {
  const [mode, setMode] = useState("monthly");
  const [buying, setBuying] = useState(null);
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  const buy = async (plan) => {
    if (plan.external_url) {
      window.location.href = plan.external_url;
      return;
    }
    setBuying(plan.id);
    try {
      localStorage.setItem("pending_checkout_plan", plan.name);
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan.lookup_key,
        origin_url: window.location.origin,
      });
      setCheckoutUrl(data.checkout_url);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBuying(null);
      setCheckoutUrl(null);
    }
  };

  return (
    <div data-testid="pricing-page" className="py-28 md:py-36 px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <Eyebrow className="mb-6">Investment Models</Eyebrow>
          <MaskedLines
            lines={["Transparent pricing.", "No surprise invoices."]}
            className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium tracking-tight leading-[1.08] text-white"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 text-slate-400 max-w-xl mx-auto"
          >
            Pay monthly while we work, or one flat fee and be done. Cancel anytime. No hidden fees, ever.
          </motion.p>
        </div>

        {/* Toggle */}
        <Reveal className="flex justify-center mb-14">
          <div className="bg-surface border border-white/10 rounded-2xl p-1.5 flex gap-1.5" data-testid="pricing-toggle">
            {[["monthly", "Monthly Plans"], ["flat", "One-Time Flat Fee"]].map(([key, label]) => (
              <button
                key={key}
                data-testid={`pricing-toggle-${key === "flat" ? "flatfee" : key}`}
                onClick={() => setMode(key)}
                className={`relative px-6 py-3 rounded-xl font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200 ${
                  mode === key ? "text-obsidian" : "text-slate-400 hover:text-white"
                }`}
              >
                {mode === key && (
                  <motion.span layoutId="pricing-pill" className="absolute inset-0 bg-gold rounded-xl" transition={{ type: "spring", stiffness: 300, damping: 30 }} />
                )}
                <span className="relative z-10">{label}</span>
              </button>
            ))}
          </div>
        </Reveal>

        {/* Cards */}
        <AnimatePresence mode="wait">
          <motion.div
            key={mode}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-24"
          >
            {PLANS[mode].map((p) => (
              <div
                key={p.id}
                data-testid={`plan-card-${p.id}`}
                className={`relative bg-[#101218] border rounded-2xl p-8 md:p-10 flex flex-col transition-all duration-300 shadow-[0_8px_32px_rgba(0,0,0,0.37)] hover:-translate-y-1 ${
                  p.popular ? "border-gold/50 shadow-[0_0_40px_rgba(226,192,132,0.12)]" : "border-white/10 hover:border-gold/30"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gold text-obsidian font-mono text-[10px] uppercase tracking-widest px-4 py-1.5 rounded-full">
                    Most Chosen
                  </span>
                )}
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">{p.tagline}</p>
                <h3 className="font-serif text-2xl text-white tracking-tight mb-4">{p.name}</h3>
                <div className="flex items-end gap-2 mb-2">
                  <span className="font-serif text-5xl text-white">${p.price.toLocaleString()}</span>
                  <span className="font-mono text-xs text-slate-500 mb-2">{mode === "monthly" ? "/ month" : "flat fee"}</span>
                </div>
                {p.setup > 0 && (
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gold/70 mb-4">+ ${p.setup} one-time audit fee</p>
                )}
                {mode === "flat" && (
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gold/70 mb-4" data-testid={`plan-annual-note-${p.id}`}>Annual plan · renews every 12 months</p>
                )}
                <div className="h-px bg-white/10 my-6" />
                <ul className="space-y-3.5 flex-1 mb-8">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-emerald mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`plan-buy-button-${p.id}`}
                  onClick={() => buy(p)}
                  disabled={buying !== null}
                  className={`w-full font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 ${
                    p.popular
                      ? "bg-gold text-obsidian hover:bg-gold-light hover:scale-[1.02] active:scale-[0.98] shadow-[0_0_20px_rgba(93,123,255,0.35)]"
                      : "bg-white/5 border border-white/15 text-white hover:bg-white/10 hover:border-gold/40"
                  } disabled:opacity-50`}
                >
                  {buying === p.id ? "Redirecting to checkout…" : "Get Started"}
                  <ArrowUpRight className="w-4 h-4" />
                </button>
                {!p.external_url && (
                  <p className="flex items-center justify-center gap-1.5 mt-3 font-mono text-[10px] uppercase tracking-widest text-slate-600">
                    <Lock className="w-3 h-3" /> Secure checkout by Stripe
                  </p>
                )}
              </div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Comparison */}
        <Reveal className="mb-24">
          <h2 className="text-2xl sm:text-3xl font-serif tracking-tight text-white text-center mb-12">
            Side by side, <span className="italic text-gold">no fine print.</span>
          </h2>
          <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#101218]" data-testid="comparison-table">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left font-mono text-[10px] uppercase tracking-widest text-slate-500 p-5">Feature</th>
                  <th className="font-mono text-[10px] uppercase tracking-widest text-slate-500 p-5">Skool Community</th>
                  <th className="font-mono text-[10px] uppercase tracking-widest text-gold p-5">Prestigious</th>
                  <th className="font-mono text-[10px] uppercase tracking-widest text-slate-500 p-5">Diamond</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map(([feature, a, b, c], i) => (
                  <tr key={feature} className={`border-b border-white/5 ${i % 2 ? "bg-white/[0.015]" : ""}`}>
                    <td className="p-5 text-slate-300">{feature}</td>
                    {[a, b, c].map((v, j) => (
                      <td key={j} className="p-5 text-center">
                        {v === true ? <Check className="w-4 h-4 text-emerald mx-auto" />
                          : v === false ? <span className="text-slate-600">✕</span>
                          : <span className="text-slate-300 font-mono text-xs">{v}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>

        {/* Compliance */}
        <Reveal>
          <div data-testid="compliance-banner" className="bg-gradient-to-br from-elevated to-surface border border-gold/25 rounded-2xl p-10 md:p-14 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(93,123,255,0.06),transparent_60%)]" />
            <h3 className="font-serif text-2xl md:text-3xl text-white mb-4 relative">Straightforward & CROA Compliant</h3>
            <p className="text-slate-400 max-w-2xl mx-auto text-sm leading-relaxed relative">
              Clear Path Repair operates in full compliance with the Credit Repair Organizations Act (CROA).
              You may cancel anytime with written notice, and you always have the right to dispute inaccurate
              items yourself at no cost. Individual results vary based on credit file complexity.
            </p>
          </div>
        </Reveal>
        {buying && (
          <div className="fixed inset-0 z-[80] bg-obsidian/95 backdrop-blur-md flex items-center justify-center" data-testid="checkout-redirect-overlay">
            <div className="text-center px-6">
              <div className="w-12 h-12 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-6" />
              <p className="font-serif text-2xl text-white mb-2">Taking you to secure checkout…</p>
              <p className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-6">Powered by Stripe · Please don't close this tab</p>
              {checkoutUrl && (
                <a href={checkoutUrl} data-testid="checkout-manual-link"
                  className="inline-block bg-gold text-obsidian hover:bg-gold-light font-mono text-xs uppercase tracking-[0.15em] px-8 py-3.5 rounded-xl transition-all">
                  Nothing happening? Tap here to open checkout
                </a>
              )}
              <button onClick={() => { setBuying(null); setCheckoutUrl(null); }} data-testid="checkout-cancel-button"
                className="block mx-auto mt-5 text-slate-500 hover:text-white font-mono text-xs uppercase tracking-widest transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
